import { ReactNode, useEffect, useMemo, useState } from "react";
import { Dialog, IconButton } from "@mui/material";
import { toast } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  ButtonPrimary,
  ButtonSecondary,
  HStack,
  SettingHeader,
  VStack,
} from "../../components/utils";
import {
  EmailComposer,
  assembleEmail,
  sanitizeEmailHtml,
} from "../../components/emailTemplateEditor";
import {
  getEmailTemplateContentV1,
  sendTestEmail,
} from "../../api/siteSettings";
import {
  useGetEmailTemplateNames,
  useGetEmailTemplateVariables,
  useSaveEmailTemplate,
} from "../../queries/siteSettings";
import { useUserDetailsV2 } from "../../queries";
import { ListOfEmailTemplates } from "../../types/SiteSettings";
import { Input, Label } from "../../components/shared/InputField";
import {
  addTemplateEditorMarkers,
  DEFAULT_EMAIL_CHROME,
  EmailChrome,
  getTemplateBody,
  getTemplateChrome,
  replaceLegacyAliases,
} from "./emailTemplateEditorHelpers";

const cloneChrome = () =>
  JSON.parse(JSON.stringify(DEFAULT_EMAIL_CHROME)) as EmailChrome;

function ChromeEditorFields({
  chrome,
  onChange,
}: {
  chrome: EmailChrome;
  onChange: (chrome: EmailChrome) => void;
}) {
  const [activeSection, setActiveSection] = useState<
    "brand" | "facts" | "cta" | "footer" | "advanced" | null
  >(null);
  const footer = chrome.footer || {};
  const update = (patch: Partial<EmailChrome>) =>
    onChange({ ...chrome, ...patch });
  const updateFooter = (patch: Partial<NonNullable<EmailChrome["footer"]>>) =>
    update({ footer: { ...footer, ...patch } });
  const updateFact = (index: number, key: "label" | "value", value: string) => {
    const factsRows = [...(chrome.factsRows || [])];
    factsRows[index] = { ...factsRows[index], [key]: value };
    update({ factsRows });
  };
  const moveFact = (index: number, direction: -1 | 1) => {
    const factsRows = [...(chrome.factsRows || [])];
    const next = index + direction;
    if (next < 0 || next >= factsRows.length) return;
    [factsRows[index], factsRows[next]] = [factsRows[next], factsRows[index]];
    update({ factsRows });
  };

  const section = (
    key: "brand" | "facts" | "cta" | "footer" | "advanced",
    title: string,
    summary: string,
    content: ReactNode
  ) => (
    <VStack className="overflow-hidden rounded-lg border border-borderColor">
      <HStack
        className="cursor-pointer items-center justify-between px-4 py-3"
        onClick={() => setActiveSection(activeSection === key ? null : key)}
      >
        <VStack>
          <span className="text-sm font-semibold text-tertiaryText">
            {title}
          </span>
          {activeSection !== key && (
            <span className="text-xs text-secondaryText">{summary}</span>
          )}
        </VStack>
        <span className="text-gray-500">
          {activeSection === key ? "⌃" : "⌄"}
        </span>
      </HStack>
      {activeSection === key && (
        <VStack className="gap-4 border-t border-borderColor p-4">
          {content}
        </VStack>
      )}
    </VStack>
  );

  const brand = (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field
          label="Brand name"
          value={chrome.brand.name}
          onChange={(value) =>
            update({ brand: { ...chrome.brand, name: value } })
          }
        />
        <Field
          label="Logo initial"
          value={chrome.brand.logoInitial || ""}
          onChange={(value) =>
            update({
              brand: { ...chrome.brand, logoInitial: value.slice(0, 2) },
            })
          }
        />
      </div>
      <Field
        label="Logo URL"
        value={chrome.brand.logoUrl || ""}
        onChange={(value) =>
          update({ brand: { ...chrome.brand, logoUrl: value } })
        }
      />
      <Field
        label="Header subtitle"
        value={chrome.headerSubtitle}
        onChange={(value) => update({ headerSubtitle: value })}
      />
    </>
  );
  const facts = (
    <>
      <HStack className="justify-end">
        <ButtonSecondary
          onClick={() =>
            update({
              factsRows: [
                ...(chrome.factsRows || []),
                { label: "", value: "" },
              ],
            })
          }
        >
          Add fact
        </ButtonSecondary>
      </HStack>
      {(chrome.factsRows || []).map((fact, index) => (
        <HStack key={index} className="items-end gap-2">
          <Field
            label="Label"
            value={fact.label}
            onChange={(value) => updateFact(index, "label", value)}
          />
          <Field
            label="Value"
            value={fact.value}
            onChange={(value) => updateFact(index, "value", value)}
          />
          <ButtonSecondary
            onClick={() =>
              update({
                factsRows: (chrome.factsRows || []).filter(
                  (_, itemIndex) => itemIndex !== index
                ),
              })
            }
          >
            Remove
          </ButtonSecondary>
        </HStack>
      ))}
    </>
  );
  const cta = (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Field
        label="Button label"
        value={chrome.cta?.label || ""}
        onChange={(value) =>
          update({ cta: { label: value, url: chrome.cta?.url || "" } })
        }
      />
      <Field
        label="Button URL"
        value={chrome.cta?.url || ""}
        onChange={(value) =>
          update({ cta: { label: chrome.cta?.label || "", url: value } })
        }
      />
    </div>
  );
  const footerFields = (
    <>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field
          label="Learn-more link text"
          value={footer.learnMore?.label || ""}
          onChange={(value) =>
            updateFooter({
              learnMore: { label: value, url: footer.learnMore?.url || "" },
            })
          }
        />
        <Field
          label="Learn-more URL"
          value={footer.learnMore?.url || ""}
          onChange={(value) =>
            updateFooter({
              learnMore: { label: footer.learnMore?.label || "", url: value },
            })
          }
        />
      </div>
      <Field
        label="Confidentiality note"
        value={footer.note || ""}
        onChange={(value) => updateFooter({ note: value })}
      />
    </>
  );
  const advanced = (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
      <Field
        label="Accent color"
        value={chrome.brand.accentColor}
        onChange={(value) =>
          update({ brand: { ...chrome.brand, accentColor: value } })
        }
      />
      <Field
        label="Header background color"
        value={chrome.brand.darkColor}
        onChange={(value) =>
          update({ brand: { ...chrome.brand, darkColor: value } })
        }
      />
      <Field
        label="Powered-by label"
        value={chrome.poweredByLabel || ""}
        onChange={(value) => update({ poweredByLabel: value })}
      />
      <Field
        label="Powered-by logo URL"
        value={chrome.poweredByLogoUrl || ""}
        onChange={(value) => update({ poweredByLogoUrl: value })}
      />
      <Field
        label="Copyright"
        value={footer.copyrightText || ""}
        onChange={(value) => updateFooter({ copyrightText: value })}
      />
      <Field
        label="Trademark"
        value={footer.trademarkText || ""}
        onChange={(value) => updateFooter({ trademarkText: value })}
      />
      <Field
        label="Unsubscribe label"
        value={footer.unsubscribe?.label || ""}
        onChange={(value) =>
          updateFooter({
            unsubscribe: { label: value, url: footer.unsubscribe?.url || "" },
          })
        }
      />
      <Field
        label="Unsubscribe URL"
        value={footer.unsubscribe?.url || ""}
        onChange={(value) =>
          updateFooter({
            unsubscribe: { label: footer.unsubscribe?.label || "", url: value },
          })
        }
      />
      <Field
        label="LinkedIn URL"
        value={footer.social?.linkedinUrl || ""}
        onChange={(value) =>
          updateFooter({ social: { ...footer.social, linkedinUrl: value } })
        }
      />
      <Field
        label="X URL"
        value={footer.social?.xUrl || ""}
        onChange={(value) =>
          updateFooter({ social: { ...footer.social, xUrl: value } })
        }
      />
    </div>
  );

  return (
    <VStack className="gap-3">
      <HStack className="items-center justify-between border-b border-borderColor px-4 py-3">
        <h3 className="text-sm font-semibold text-tertiaryText">
          Configuration
        </h3>
        <span className="text-xs text-secondaryText">
          Header, facts, CTA and footer
        </span>
      </HStack>
      {section(
        "brand",
        "Brand & Header",
        `${chrome.brand.name} · ${chrome.headerSubtitle}`,
        brand
      )}
      {section(
        "facts",
        "Facts Strip",
        `${chrome.factsRows?.length || 0} facts`,
        facts
      )}
      {section("cta", "Call to Action", chrome.cta?.label || "No button", cta)}
      {section("footer", "Footer", footer.note || "Nothing set", footerFields)}
      {section(
        "advanced",
        "Advanced Configuration",
        "Colors, legal text and social links",
        advanced
      )}
    </VStack>
  );
}

function Field({
  light = false,
  label,
  value,
  onChange,
}: {
  light?: boolean;
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <VStack className="gap-1 flex-1">
      <Label className={light ? "!text-white" : ""}>{label}</Label>
      <Input value={value} onChange={(event) => onChange(event.target.value)} />
    </VStack>
  );
}

function ChromeEditor({
  chrome,
  onChange,
}: {
  chrome: EmailChrome;
  onChange: (chrome: EmailChrome) => void;
}) {
  const [open, setOpen] = useState(false);
  const factsCount = chrome.factsRows?.length || 0;
  const summary = `“${chrome.headerSubtitle}” · ${factsCount} fact${
    factsCount === 1 ? "" : "s"
  }${chrome.cta?.label ? " · CTA set" : ""}`;

  return (
    <>
      <HStack className="items-center justify-between rounded-lg border border-borderColor bg-white px-4 py-3">
        <VStack className="gap-1">
          <h3 className="text-sm font-semibold text-tertiaryText">
            Header, Facts &amp; Footer
          </h3>
          <span className="text-xs text-secondaryText">{summary}</span>
        </VStack>
        <HStack className="gap-2">
          <ButtonSecondary onClick={() => onChange(cloneChrome())}>
            Reset to defaults
          </ButtonSecondary>
          <ButtonSecondary onClick={() => setOpen(true)}>Edit</ButtonSecondary>
        </HStack>
      </HStack>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <VStack className="gap-4 p-6">
          <HStack className="justify-between">
            <VStack className="gap-1">
              <h3 className="text-lg font-semibold text-tertiaryText">
                Header, Facts &amp; Footer
              </h3>
              <span className="text-xs text-secondaryText">
                The branded scaffolding wrapped around every email of this kind.
              </span>
            </VStack>
            <IconButton aria-label="Close" onClick={() => setOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </HStack>
          <ChromeEditorFields chrome={chrome} onChange={onChange} />
          <HStack className="justify-between">
            <ButtonSecondary onClick={() => onChange(cloneChrome())}>
              Reset to defaults
            </ButtonSecondary>
            <ButtonPrimary onClick={() => setOpen(false)}>Done</ButtonPrimary>
          </HStack>
        </VStack>
      </Dialog>
    </>
  );
}

function EmailTemplateEditor({
  initialTemplateId,
  onViewAll,
}: {
  initialTemplateId: string;
  onViewAll: () => void;
}) {
  const { data: templates = [], refetch } = useGetEmailTemplateNames();
  const { data: userDetails } = useUserDetailsV2();
  const { mutate: saveTemplate, isLoading: saving } = useSaveEmailTemplate();
  const [templateId, setTemplateId] = useState("");
  const [rawTemplate, setRawTemplate] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [chrome, setChrome] = useState<EmailChrome>(cloneChrome);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [hasSaved, setHasSaved] = useState(false);

  const selectedTemplate = templates.find(
    (template) => template.id === templateId
  ) as ListOfEmailTemplates | undefined;
  const { data: variables = [], isFetched: variablesFetched } =
    useGetEmailTemplateVariables(selectedTemplate?.templateType || "");
  const editorTokens = useMemo(
    () =>
      variables.map(({ variable }) => ({ label: variable, token: variable })),
    [variables]
  );
  const sampleValues = useMemo(
    () =>
      variables.reduce(
        (values, { variable, exampleValue }) => ({
          ...values,
          [variable]: exampleValue,
        }),
        {} as Record<string, string>
      ),
    [variables]
  );

  useEffect(() => {
    if (!rawTemplate || !variablesFetched) return;
    const templateBody = getTemplateBody(rawTemplate);
    if (templateBody === null) return;
    setBody(replaceLegacyAliases(templateBody, variables));
    setChrome(
      JSON.parse(
        replaceLegacyAliases(
          JSON.stringify(getTemplateChrome(rawTemplate)),
          variables
        )
      ) as EmailChrome
    );
  }, [rawTemplate, variables, variablesFetched]);

  const markDirty = () => setHasSaved(false);
  const loadTemplate = async (template: ListOfEmailTemplates) => {
    setTemplateId(template.id);
    setRawTemplate(null);
    setBody("");
    setSubject(template.subject || "");
    setChrome(cloneChrome());
    markDirty();
    setLoadingTemplate(true);
    try {
      const response = await getEmailTemplateContentV1(template.id);
      setRawTemplate(response.content || "");
      setSubject(response.subject || template.subject || "");
    } catch {
      toast("Failed to load email template", { type: "error" });
    } finally {
      setLoadingTemplate(false);
    }
  };

  useEffect(() => {
    const template = templates.find(({ id }) => id === initialTemplateId);
    if (template && template.id !== templateId) loadTemplate(template);
  }, [initialTemplateId, templates]);

  const handleSave = () => {
    if (!selectedTemplate || !subject.trim() || !body.trim()) {
      toast("Subject and email body are required", { type: "error" });
      return;
    }
    const cleanBody = sanitizeEmailHtml(body);
    const assembled = assembleEmail({ chrome, subject, bodyHtml: cleanBody });
    const html = addTemplateEditorMarkers(assembled.html, cleanBody, chrome);
    saveTemplate(
      {
        templateId: selectedTemplate.id,
        subject,
        emailTemplateBodyBase64: btoa(unescape(encodeURIComponent(html))),
      },
      {
        onSuccess: () => {
          setHasSaved(true);
          setRawTemplate(html);
          toast("Template saved successfully", { type: "success" });
          refetch();
        },
        onError: (error: any) => {
          toast(error?.response?.data?.errorRaw || "Failed to save template", {
            type: "error",
          });
        },
      }
    );
  };

  return (
    <Box className="w-full p-4 bg-white rounded-lg">
      <VStack className="gap-6 p-4">
        <HStack className="items-start justify-between gap-4">
          <SettingHeader
            title="Email Template Editor"
            description="Edit a template selected from Email Template Settings"
          />
          <ButtonSecondary onClick={onViewAll}>
            View all templates
          </ButtonSecondary>
        </HStack>
        {!initialTemplateId && (
          <Box className="rounded-md border border-borderColor bg-slate-50 p-4 text-sm text-secondaryText">
            Choose a template from Email Template Settings, then select Open in
            Email Template Editor.
          </Box>
        )}
        {loadingTemplate && (
          <Box className="text-sm text-gray-dark">Loading template...</Box>
        )}
        {rawTemplate && getTemplateBody(rawTemplate) === null && (
          <Box className="p-4 text-sm text-yellow-800 bg-yellow-100 border border-yellow-300 rounded-md">
            This template does not include editable body markers. Use Email
            Template Settings to edit it safely.
          </Box>
        )}
        {rawTemplate &&
          getTemplateBody(rawTemplate) !== null &&
          variablesFetched && (
            <>
              <EmailComposer
                editorHeader={
                  <ChromeEditor
                    chrome={chrome}
                    onChange={(value) => {
                      setChrome(value);
                      markDirty();
                    }}
                  />
                }
                subject={subject}
                body={body}
                onSubjectChange={(value) => {
                  setSubject(value);
                  markDirty();
                }}
                onBodyChange={(value) => {
                  setBody(value);
                  markDirty();
                }}
                chrome={chrome}
                recipients={[
                  {
                    id: "sample-recipient",
                    displayName: "Sample recipient",
                    values: sampleValues,
                  },
                ]}
                senderName="Hissa Equity Team"
                testEmail={userDetails?.emailId || "your email"}
                tokens={editorTokens}
                onSendTest={
                  hasSaved
                    ? () =>
                        sendTestEmail(selectedTemplate!.templateType)
                          .then(() =>
                            toast("Test email sent successfully", {
                              type: "success",
                            })
                          )
                          .catch(() =>
                            toast("Failed to send test email", {
                              type: "error",
                            })
                          )
                    : undefined
                }
                sendTestEnabled={hasSaved}
              />
              <HStack className="justify-between gap-3">
                <ButtonSecondary
                  onClick={() => {
                    setTemplateId("");
                    setRawTemplate(null);
                    setBody("");
                    setSubject("");
                    setChrome(cloneChrome());
                    markDirty();
                  }}
                >
                  Cancel
                </ButtonSecondary>
                <ButtonPrimary loading={saving} onClick={handleSave}>
                  Save Template
                </ButtonPrimary>
              </HStack>
            </>
          )}
      </VStack>
    </Box>
  );
}

export default EmailTemplateEditor;
